import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import axios from "axios";
import PageHeader from "../Layouts/PageHeader";
import { Svg, UiIcon, CheckMark } from "../Layouts/Icon";
import { gridPath, iconLabel } from "../utils/svgIcon";
import { iconStyle } from "../utils/iconStyle";
import { searchIcons } from "../utils/searchIcons";
import { API_URL, STUDIO_PATH } from "../constants";
import { Link } from "react-router-dom";
import { readCustomIcons, removeCustomIcon, clearCustomIcons, onCustomIconsChange } from "../utils/customIcons";
import classes from "./composer.scss";

const FORMATS = [
  { id: "woff2", label: "WOFF2", note: "All modern browsers" },
  { id: "woff", label: "WOFF", note: "Older browsers" },
  { id: "ttf", label: "TTF", note: "Desktop apps, Android" },
];

const EXTRAS = [
  { id: "css", label: "CSS" },
  { id: "minCss", label: "Minified CSS" },
  { id: "demo", label: "Demo page" },
];

const STYLES = [
  { id: "all", label: "All" },
  { id: "line", label: "Line" },
  { id: "solid", label: "Solid" },
  { id: "duotone", label: "Duotone" },
];

// Same rule as the server: what's safe as a file, folder and font-family name
const SLUG = /^[a-z][a-z0-9-]{0,39}$/;
const toSlug = (text) =>
  text
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

// Bytes per glyph in each format, measured on the full IcoFont build
const BYTES_PER_GLYPH = {
  woff2: 226,
  woff: 273,
  ttf: 442,
};
const formatSize = (bytes) =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1024 * 1024
      ? `${Math.round(bytes / 1024)} KB`
      : `${(bytes / 1048576).toFixed(1)} MB`;

const PAGE = 240;
const DEFAULT_SETTINGS = {
  name: "my-icofont",
  prefix: "icofont",
  formats: ["woff2", "woff"],
  css: true,
  minCss: true,
  demo: true,
};

const readSettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem("composer-settings"));
    if (!saved) return DEFAULT_SETTINGS;
    // Formats no longer offered (EOT, SVG font) drop out of older saved settings
    const formats = (saved.formats || []).filter((id) => FORMATS.some((format) => format.id === id));
    return { ...DEFAULT_SETTINGS, ...saved, formats: formats.length ? formats : DEFAULT_SETTINGS.formats };
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
};

const saveSettings = (settings) => {
  try {
    window.localStorage.setItem("composer-settings", JSON.stringify(settings));
  } catch (e) {
    // Not remembered this time; nothing else depends on it
  }
};

// Converting a path to the 24 grid is the costly part of drawing an icon; do it once per icon
const pathCache = {};
const pathOf = (icon) =>
  pathCache[icon.id] || (pathCache[icon.id] = gridPath(icon.svg));

const Glyph = ({ icon, secondary }) =>
  icon && icon.svg ? <Svg path={pathOf(icon)} pathSecondary={secondary ? pathOf(secondary) : null} /> : null;

Glyph.propTypes = {
  icon: PropTypes.object,
  secondary: PropTypes.object,
};

const Toggle = ({ checked, onChange, children, note }) => (
  <label className={classes.check}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className={classes.checkBox} aria-hidden="true">
      <CheckMark />
    </span>
    <span className={classes.checkText}>
      {children}
      {note ? <small>{note}</small> : null}
    </span>
  </label>
);

Toggle.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  note: PropTypes.string,
};

export const Composer = ({ icons, selectedIds, selectionReady, setCollection, location }) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [style, setStyle] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [filter, setFilter] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [shared, setShared] = useState(null); // a shared link's icons and settings, waiting for a choice
  const [build, setBuild] = useState({ status: "idle", message: "" });
  const [copied, setCopied] = useState(false);
  const lastClicked = useRef(null);
  // Icons designed in the studio, kept in this browser
  const [customIcons, setCustomIcons] = useState([]);
  const linkRead = useRef(false);
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const [panelInView, setPanelInView] = useState(false);

  // Tiles: one per icon (a duotone icon's second layer rides along with the first)
  const tiles = useMemo(
    () => icons.filter((icon) => icon.assign !== "after"),
    [icons],
  );
  const secondaries = useMemo(() => {
    const byName = {};
    icons.forEach((icon) => {
      if (icon.isDuotone && icon.assign === "after") byName[icon.name] = icon;
    });
    return byName;
  }, [icons]);
  const byId = useMemo(() => {
    const map = {};
    tiles.forEach((icon) => {
      map[icon.id] = icon;
    });
    return map;
  }, [tiles]);
  const categories = useMemo(
    () =>
      tiles
        .map((icon) => icon.cat)
        .filter((cat, index, all) => all.indexOf(cat) === index),
    [tiles],
  );

  const selected = useMemo(
    () => selectedIds.map((id) => byId[id]).filter(Boolean),
    [selectedIds, byId],
  );
  const selectedSet = useMemo(
    () => new Set(selected.map((icon) => icon.id)),
    [selected],
  );

  const results = useMemo(() => {
    const pool = query.trim() ? searchIcons(tiles, query) : tiles;
    return pool.filter(
      (icon) =>
        (category === "all" || icon.cat === category) &&
        (style === "all" || iconStyle(icon) === style),
    );
  }, [tiles, query, category, style]);
  const shown = results.slice(0, limit);
  const allShownSelected =
    results.length > 0 && results.every((icon) => selectedSet.has(icon.id));

  useEffect(() => {
    setSettings(readSettings());
    setCustomIcons(readCustomIcons());
    return onCustomIconsChange(setCustomIcons);
  }, []);

  // A shared link: ?icons=<codes>&name=&prefix=. Codes never change, so links keep working.
  // Read it once both the icons and the saved selection have loaded
  useEffect(() => {
    if (linkRead.current || !tiles.length || !selectionReady || !location || !location.search) return;
    linkRead.current = true;
    const params = new URLSearchParams(location.search);
    const codes = (params.get("icons") || "").split(",").filter(Boolean);
    const linked = codes
      .map((code) => tiles.find((icon) => icon.code === code))
      .filter(Boolean);
    const name = params.get("name");
    const prefix = params.get("prefix");
    const linkSettings = {
      ...(name && SLUG.test(name) ? { name } : {}),
      ...(prefix && SLUG.test(prefix) ? { prefix } : {}),
    };
    if (!linked.length) return;
    const same =
      linked.length === selectedIds.length &&
      linked.every((icon) => selectedIds.indexOf(icon.id) !== -1);
    if (same) return;
    if (selectedIds.length === 0) {
      setCollection(linked);
      updateSettings(linkSettings);
    } else setShared({ icons: linked, settings: linkSettings });
  }, [tiles.length, selectionReady]);

  // "Clear all" asks once more, then lapses
  useEffect(() => {
    if (!confirmClear) return undefined;
    const timer = setTimeout(() => setConfirmClear(false), 4000);
    return () => clearTimeout(timer);
  }, [confirmClear]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (build.status !== "done") return undefined;
    const timer = setTimeout(
      () => setBuild({ status: "idle", message: "" }),
      2400,
    );
    return () => clearTimeout(timer);
  }, [build.status]);

  // The bottom bar on phones only matters while the font panel is off screen
  useEffect(() => {
    if (!panelRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => setPanelInView(entries[0].isIntersecting));
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, []);

  function updateSettings(patch) {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveSettings(next);
      return next;
    });
  }

  const resetBrowse = () => setLimit(PAGE);

  // Click toggles one icon; shift-click sets every icon from the last one clicked to this one
  const onTile = (icon, index, event) => {
    const adding = !selectedSet.has(icon.id);
    let range = [icon];
    if (event.shiftKey && lastClicked.current !== null) {
      const from = Math.min(lastClicked.current, index);
      const to = Math.max(lastClicked.current, index);
      range = shown.slice(from, to + 1);
    }
    lastClicked.current = index;
    const ids = new Set(range.map((item) => item.id));
    if (adding)
      setCollection(
        selected.concat(range.filter((item) => !selectedSet.has(item.id))),
      );
    else setCollection(selected.filter((item) => !ids.has(item.id)));
  };

  const toggleAllShown = () => {
    if (allShownSelected) {
      const ids = new Set(results.map((icon) => icon.id));
      setCollection(selected.filter((icon) => !ids.has(icon.id)));
    } else {
      setCollection(
        selected.concat(results.filter((icon) => !selectedSet.has(icon.id))),
      );
    }
  };

  const remove = (icon) =>
    setCollection(selected.filter((item) => item.id !== icon.id));

  const clearAll = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setFilter("");
    setCollection([]);
    clearCustomIcons();
  };

  const total = selected.length + customIcons.length;

  const nameOk = SLUG.test(settings.name);
  const prefixOk = SLUG.test(settings.prefix);
  const hasStyles = settings.css || settings.minCss;
  const glyphCount =
    selected.reduce((count, icon) => count + (icon.isDuotone ? 2 : 1), 0) +
    customIcons.reduce((count, icon) => count + (icon.pathSecondary ? 2 : 1), 0);
  // A custom icon can't share a class name with an IcoFont icon in the same font
  const clash = customIcons.find((custom) => selected.some((icon) => icon.name === custom.name));
  const estimate = settings.formats.map((format) => ({
    format,
    bytes: glyphCount * BYTES_PER_GLYPH[format] + 300,
  }));
  const problem = !total
    ? "Pick at least one icon."
    : clash
      ? `Your custom “${clash.name}” has the same name as an IcoFont icon here. Rename it in the studio.`
      : !nameOk
      ? "Font name: start with a letter; use lowercase letters, numbers and hyphens."
      : !prefixOk
        ? "Class prefix: start with a letter; use lowercase letters, numbers and hyphens."
        : !settings.formats.length
          ? "Pick at least one font format."
          : "";

  const download = () => {
    if (problem || build.status === "building") return;
    setBuild({ status: "building", message: "" });
    axios
      .post(API_URL + "/api/iconfont/compose", {
        ids: selected.map((icon) => icon.id),
        custom: customIcons.map(({ name, path, pathSecondary }) => ({ name, path, pathSecondary })),
        name: settings.name,
        prefix: settings.prefix,
        formats: settings.formats,
        css: settings.css,
        minCss: settings.minCss,
        demo: settings.demo,
      })
      .then(({ data }) => {
        window.location = `/process/download?uid=${data.uuid}&name=${encodeURIComponent(data.name)}`;
        setBuild({ status: "done", message: "" });
      })
      .catch((error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Couldn’t build the font. Please try again.";
        setBuild({ status: "error", message });
      });
  };

  const shareLink = () => {
    const params = new URLSearchParams();
    params.set("icons", selected.map((icon) => icon.code).join(","));
    if (settings.name !== DEFAULT_SETTINGS.name)
      params.set("name", settings.name);
    if (settings.prefix !== DEFAULT_SETTINGS.prefix)
      params.set("prefix", settings.prefix);
    const url = `${window.location.origin}${window.location.pathname}?${params.toString().replace(/%2C/g, ",")}`;
    const done = () => setCopied(true);
    if (navigator.clipboard && navigator.clipboard.writeText)
      navigator.clipboard.writeText(url).then(done, done);
    else done();
    if (window.history && window.history.replaceState)
      window.history.replaceState(null, "", url);
  };

  const listed = filter.trim() ? searchIcons(selected, filter) : selected;
  const example =
    selected[0] ||
    (customIcons[0] && { name: customIcons[0].name, isDuotone: !!customIcons[0].pathSecondary });
  const exampleClass = example
    ? `${example.isDuotone ? `${settings.prefix}-duotone ` : ""}${settings.prefix}-${example.name}`
    : `${settings.prefix}-home`;
  const cssFile = `${settings.name}/${settings.name}${settings.minCss ? ".min" : ""}.css`;

  let library;
  if (!tiles.length) {
    library = (
      <div className={classes.status} role="status">
        Loading icons…
      </div>
    );
  } else if (!results.length) {
    library = (
      <div className={classes.status} role="status">
        <p>
          No icons match{query.trim() ? ` “${query.trim()}”` : ""} with these
          filters.
        </p>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => {
            setQuery("");
            setCategory("all");
            setStyle("all");
            resetBrowse();
          }}
        >
          Reset filters
        </button>
      </div>
    );
  } else {
    library = (
      <div>
        <ul className={classes.grid} aria-label="Icons">
          {shown.map((icon, index) => {
            const on = selectedSet.has(icon.id);
            const label = iconLabel(icon.name);
            return (
              <li key={icon.id}>
                <button
                  type="button"
                  className={`${classes.tile}${on ? ` ${classes.on}` : ""}`}
                  aria-pressed={on}
                  title={`${label}${on ? " (in your font)" : ""}`}
                  onClick={(event) => onTile(icon, index, event)}
                >
                  <Glyph icon={icon} secondary={secondaries[icon.name]} />
                  <span className={classes.tileName}>{label}</span>
                  {on ? (
                    <span className={classes.tileCheck} aria-hidden="true">
                      <CheckMark />
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
        {results.length > limit ? (
          <div className={classes.more}>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setLimit(limit + PAGE)}
            >
              Show more
            </button>
            <span>
              {shown.length} of {results.length}
            </span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <PageHeader label="Composer" title="Build your own icon font" />
      <div className={classes.page}>
        <Helmet>
          <title>Font Composer: Build Your Own Icon Font | IcoFont</title>
          <meta
            name="description"
            content="Pick the IcoFont icons you need and download them as your own lightweight icon font, with your font name, class prefix and formats."
          />
        </Helmet>

        {shared ? (
          <div className={classes.notice} role="status">
            <span>
              This link has <strong>{shared.icons.length}</strong>{" "}
              {shared.icons.length === 1 ? "icon" : "icons"}. Use them instead of the{" "}
              {selected.length} you’ve picked?
            </span>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setCollection(shared.icons);
                  updateSettings(shared.settings);
                  setShared(null);
                }}
              >
                Use the link’s icons
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setShared(null)}
              >
                Keep mine
              </button>
            </div>
          </div>
        ) : null}

        <div className={classes.layout}>
          <section
            aria-labelledby="composer-library"
          >
            <div className={classes.libraryHead}>
              <h2 id="composer-library">Icons</h2>
              {results.length ? (
                <button
                  type="button"
                  className={classes.link}
                  onClick={toggleAllShown}
                >
                  {allShownSelected
                    ? `Remove these ${results.length}`
                    : `Add all ${results.length}`}
                </button>
              ) : null}
            </div>
            <div className={classes.filters}>
              <div className={classes.search}>
                <UiIcon name="search" />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  placeholder="Search icons"
                  aria-label="Search icons"
                  autoComplete="off"
                  spellCheck="false"
                  onChange={(event) => {
                    setQuery(event.target.value);
                    resetBrowse();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape" && query) {
                      event.preventDefault();
                      setQuery("");
                      resetBrowse();
                    }
                  }}
                />
                {query ? (
                  <button
                    type="button"
                    className={classes.searchClear}
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery("");
                      resetBrowse();
                      searchRef.current.focus();
                    }}
                  >
                    <UiIcon name="close-line" />
                  </button>
                ) : null}
              </div>
              <select
                className={classes.select}
                value={category}
                aria-label="Category"
                onChange={(event) => {
                  setCategory(event.target.value);
                  resetBrowse();
                }}
              >
                <option value="all">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {iconLabel(cat)}
                  </option>
                ))}
              </select>
              <div className="segmented" role="group" aria-label="Icon style">
                {STYLES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={style === option.id}
                    onClick={() => {
                      setStyle(option.id);
                      resetBrowse();
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <p className={classes.hint}>
              Click an icon to add or remove it.
              <span className={classes.hintShift}> Shift-click to add a whole run.</span>
            </p>
            {library}
          </section>

          <aside
            ref={panelRef}
            className={classes.panel}
            id="your-font"
            aria-labelledby="composer-font"
          >
            <div className={classes.panelHead}>
              <h2 id="composer-font">
                Your font{" "}
                <span className={classes.count}>{total}</span>
              </h2>
              {total ? (
                <button
                  type="button"
                  className={`${classes.link}${confirmClear ? ` ${classes.danger}` : ""}`}
                  onClick={clearAll}
                >
                  {confirmClear ? "Click again to clear" : "Clear all"}
                </button>
              ) : null}
            </div>

            {total ? (
              <div className={classes.picked}>
                {selected.length > 24 ? (
                  <input
                    type="search"
                    className={classes.pickedFilter}
                    value={filter}
                    placeholder={`Filter ${selected.length} icons`}
                    aria-label="Filter your icons"
                    onChange={(event) => setFilter(event.target.value)}
                  />
                ) : null}
                <ul
                  className={classes.pickedGrid}
                  aria-label="Icons in your font"
                >
                  {customIcons.map((icon) => (
                    <li key={`custom-${icon.name}`}>
                      <button
                        type="button"
                        className={`${classes.pickedTile} ${classes.custom}`}
                        title={`Remove ${icon.name} (your own icon)`}
                        aria-label={`Remove ${icon.name}, your own icon`}
                        onClick={() => removeCustomIcon(icon.name)}
                      >
                        <Svg path={icon.path} pathSecondary={icon.pathSecondary} />
                        <span className={classes.pickedX} aria-hidden="true">
                          <UiIcon name="close-line" />
                        </span>
                      </button>
                    </li>
                  ))}
                  {listed.map((icon) => (
                    <li key={icon.id}>
                      <button
                        type="button"
                        className={classes.pickedTile}
                        title={`Remove ${iconLabel(icon.name)}`}
                        aria-label={`Remove ${iconLabel(icon.name)}`}
                        onClick={() => remove(icon)}
                      >
                        <Glyph icon={icon} secondary={secondaries[icon.name]} />
                        <span className={classes.pickedX} aria-hidden="true">
                          <UiIcon name="close-line" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {filter.trim() && !listed.length ? (
                  <p className={classes.muted}>
                    No icons match “{filter.trim()}”.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className={classes.empty}>
                <UiIcon name="duotone:user-notes" />
                <p>
                  Pick icons from the list to start your font, or{" "}
                  <Link to={STUDIO_PATH}>design your own</Link>.
                </p>
              </div>
            )}

            <div className={classes.fields}>
              <div className={classes.row}>
                <label className={classes.field}>
                  <span>Font name</span>
                  <input
                    type="text"
                    value={settings.name}
                    maxLength={40}
                    spellCheck="false"
                    aria-invalid={!nameOk}
                    onChange={(event) =>
                      updateSettings({ name: toSlug(event.target.value) })
                    }
                  />
                </label>
                <label className={classes.field}>
                  <span>Class prefix</span>
                  <input
                    type="text"
                    value={settings.prefix}
                    maxLength={40}
                    spellCheck="false"
                    aria-invalid={!prefixOk}
                    onChange={(event) =>
                      updateSettings({ prefix: toSlug(event.target.value) })
                    }
                  />
                </label>
              </div>

              <fieldset className={classes.group}>
                <legend>Font formats</legend>
                {FORMATS.map((format) => (
                  <Toggle
                    key={format.id}
                    note={format.note}
                    checked={settings.formats.indexOf(format.id) !== -1}
                    onChange={(on) =>
                      updateSettings({
                        formats: FORMATS.map((item) => item.id).filter((id) =>
                          id === format.id
                            ? on
                            : settings.formats.indexOf(id) !== -1,
                        ),
                      })
                    }
                  >
                    {format.label}
                  </Toggle>
                ))}
              </fieldset>

              <fieldset className={classes.group}>
                <legend>Also include</legend>
                {EXTRAS.map((extra) => (
                  <Toggle
                    key={extra.id}
                    checked={!!settings[extra.id]}
                    onChange={(on) => updateSettings({ [extra.id]: on })}
                  >
                    {extra.label}
                  </Toggle>
                ))}
              </fieldset>
            </div>

            <div className={classes.usage}>
              <span className={classes.usageLabel}>Use it</span>
              <pre>
                <code>
                  {hasStyles
                    ? `<link rel="stylesheet" href="${cssFile}">\n`
                    : ""}
                  {`<i class="${exampleClass}"></i>`}
                </code>
              </pre>
            </div>

            <div className={classes.foot}>
              {total && settings.formats.length ? (
                <p className={classes.estimate}>
                  About{" "}
                  {estimate.map((item, index) => (
                    <span key={item.format}>
                      {index ? ", " : ""}
                      <strong>{formatSize(item.bytes)}</strong>{" "}
                      {item.format.toUpperCase()}
                    </span>
                  ))}
                </p>
              ) : null}
              <button
                type="button"
                className={`btn btn-primary ${classes.download}`}
                onClick={download}
                disabled={!!problem || build.status === "building"}
                aria-describedby={problem ? "composer-problem" : undefined}
              >
                {build.status === "building" ? (
                  "Building your font…"
                ) : build.status === "done" ? (
                  <span className={classes.done}>
                    <CheckMark /> Download started
                  </span>
                ) : (
                  <span className={classes.done}>
                    <UiIcon name="download" /> Download font
                  </span>
                )}
              </button>
              {problem && total ? (
                <p className={classes.problem} id="composer-problem">
                  {problem}
                </p>
              ) : null}
              {build.status === "error" ? (
                <p className={classes.problem} role="alert">
                  {build.message}
                </p>
              ) : null}
              {selected.length ? (
                <button
                  type="button"
                  className={classes.share}
                  onClick={shareLink}
                >
                  {copied ? (
                    <span className={classes.done}>
                      <CheckMark /> Link copied
                    </span>
                  ) : (
                    <span className={classes.done}>
                      <UiIcon name="link" /> Copy a link to this set
                    </span>
                  )}
                </button>
              ) : null}
              {customIcons.length ? (
                <p className={classes.estimate}>
                  {selected.length ? "Links carry the IcoFont icons only; your own icons stay in this browser." : "Your own icons stay in this browser."}{" "}
                  <Link to={STUDIO_PATH}>Open the studio</Link>
                </p>
              ) : null}
            </div>
          </aside>
        </div>

        {/* Phones: the font panel sits below the list, so keep its count and a way there in reach */}
        {total && !panelInView ? (
          <a href="#your-font" className={classes.jump}>
            <span>
              <strong>{total}</strong>{" "}
              {total === 1 ? "icon" : "icons"} in your font
            </span>
            <span>Review &amp; download</span>
          </a>
        ) : null}
      </div>
    </div>
  );
};

Composer.propTypes = {
  icons: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  selectionReady: PropTypes.bool,
  setCollection: PropTypes.func.isRequired,
  location: PropTypes.object,
};

export default Composer;
